import styled from '@emotion/styled';
import { useMemoizedFn } from 'ahooks';
import { Checkbox } from 'antd';
import { compact, uniq, without } from 'lodash-es';
import { ReactNode, useRef } from 'react';

const INTENT_WIDTH = 18;
const INTENT_OFFSET = 8;

const Ul = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #f0f0f0;
  > li {
    margin: 0;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }
  > section {
    display: flex;
    flex-wrap: wrap;
    padding-bottom: 16px;
    > li {
      margin: 16px 0 0 40px;
      > div {
        padding-left: 24px;
      }
    }
  }
  label > span + span {
    color: rgba(0, 0, 0, 0.85);
  }
`;

export interface Node<T> {
  value: T extends number | string ? T : number | string;
  label: string | ReactNode;
  key: string;
  labelExtra?: ReactNode;
  disabled?: boolean;
  children?: Node<T>[];
  /**
   * 表示该节点为虚构，不存在于实际数据中
   */
  virtual?: boolean;
  /**
   * 由于virtual node 存在(实际数据中没有)，
   * 因此raw也可能为空
   */
  raw?: T;
}

interface RenderedNode<T> extends Node<T> {
  parentChecked?: boolean;
  parentDisabled?: boolean;
}

const IntentBlock = styled.span`
  display: inline-block;
`;
export interface CheckboxTreeProps<T> {
  className?: string;
  dataSource: Node<T>;
  selectedKeys?: Node<T>['key'][];
  disabled?: boolean;
  /**
   * 自定义label的render逻辑
   */
  labelRender?: (node: RenderedNode<T>, checked: boolean, disabled?: boolean) => ReactNode;
  labelSuffixRender?: (node: RenderedNode<T>, checked: boolean, disabled?: boolean) => ReactNode;
  /**
   * 自定义labelExtra的render逻辑
   */
  labelExtraRender?: (node: RenderedNode<T>, checked: boolean, disabled?: boolean) => ReactNode;
  onChange?: (
    selectedKeys: Node<T>['key'][],
    selectedNodes: Node<T>[],
    event: {
      selectedDataSet: T[];
      node: Node<T>;
      checked: boolean;
    },
  ) => void;
}
const calcHasChild = (node: Node<any>) => (node?.children?.length ?? 0) > 0;

const calcCheckStatus = (
  node: RenderedNode<any>,
  selectedKeys: CheckboxTreeProps<any>['selectedKeys'] = [],
) => {
  const hasChild = calcHasChild(node);
  const res = {
    checked: false,
    indeterminate: false,
  };
  if (selectedKeys.length < 1) {
    return res;
  }
  if (node.parentChecked || selectedKeys.includes(node.key)) {
    res.checked = true;
    return res;
  }
  if (!hasChild) {
    return res;
  }
  const someNotCheck = node.children!.some((n) => {
    return !calcCheckStatus(n, selectedKeys).checked;
  });
  res.checked = !someNotCheck;
  res.indeterminate = !res.checked;
  return res;
};

const getAllNodesOfSelectedKeys = (dataSource: Node<any>, selectedKeys?: Node<any>['key'][]) => {
  if (!selectedKeys || selectedKeys?.length < 1) {
    return [];
  }
  let keys = [...selectedKeys];
  const res: Node<any>[] = [];
  const walk = (node: Node<any>) => {
    if (keys.includes(node.key)) {
      res.push(node);
      keys = without(keys, node.key);
      keys = keys.concat(node?.children?.map((n) => n.key) ?? []);
    }
    node?.children?.forEach(walk);
  };
  walk(dataSource);
  return res;
};
const CheckboxTree = <T extends any>(props: CheckboxTreeProps<T>) => {
  const {
    className,
    labelRender,
    labelSuffixRender,
    dataSource,
    selectedKeys = [],
    onChange,
    labelExtraRender,
    disabled: compDisabled,
  } = props;
  const nodeKeyGraphRef = useRef<
    Record<string, { parent: Node<T> | null; children: Node<T>['children'] }>
  >({});
  const calcFinalSelectedKeys = useMemoizedFn((node: Node<any>, checked: boolean) => {
    const calc = (
      node: Node<any>,
      checked: boolean,
      selectedKeys: CheckboxTreeProps<T>['selectedKeys'] = [],
    ): Required<CheckboxTreeProps<T>>['selectedKeys'] => {
      const parent = nodeKeyGraphRef.current[node.key]?.parent;
      const siblings = parent?.children;
      const removalchildrenKeys =
        node.children
          // 如果节点是disabled的话，其状态不受父节点状态影响
          ?.filter((n) => !n?.disabled)
          ?.map?.((n) => n.key) || [];
      // 无论当前node是选中还是非选中，与当前key相关的子key要不全选，要不全取消
      // 因此可以直接删除所有的相关的子key
      let next = without<string>([...selectedKeys], ...removalchildrenKeys);
      if (checked) {
        // #region deal with checked
        next.push(node.key);
        if (parent?.children && parent.children.length > 0) {
          const someSiblingsNotChecked = siblings?.some?.((n) => !next.includes(n.key));
          const allSiblingsChecked = !someSiblingsNotChecked;
          if (allSiblingsChecked) {
            next.push(parent.key);
            next = calc(parent, checked, next);
            return next;
          }
        }
        return next;

        // #endregion deal with checked
      }
      // #region  deal with  !checked

      const isSelfSelected = selectedKeys.includes(node.key);
      if (isSelfSelected) {
        next = without(next, node.key);
      } else {
        const findSelectedParent = () => {
          if (!parent) {
            return node;
          }
          let p: Node<any> | null = parent;
          while (p) {
            if (selectedKeys.includes(p.key)) {
              return p;
            }
            p = nodeKeyGraphRef.current[p.key].parent;
          }
          return node;
        };
        const selectedParent = findSelectedParent();
        next = without(next, selectedParent.key);
      }
      if (!isSelfSelected && parent?.children && parent.children.length > 0) {
        siblings?.forEach?.((n) => {
          if (n.key === node.key) {
            return;
          }
          next = next.concat(calc(n, true, next));
        });
      }

      // #endregion  deal with  !checked

      return next;
    };
    return uniq(calc(node, checked, selectedKeys));
  });
  const nodeRender = useMemoizedFn(
    (node: RenderedNode<T>, parent: Node<T> | null = null, deep = 0) => {
      const { labelExtra, key, disabled, parentDisabled } = node;
      const isRoot = deep === 0;
      const hasChild = calcHasChild(node);
      const intent = (
        <IntentBlock style={{ width: `${(INTENT_WIDTH + INTENT_OFFSET) * deep}px` }} />
      );
      const { indeterminate, checked } = calcCheckStatus(node, selectedKeys);

      if (isRoot) {
        nodeKeyGraphRef.current = {};
      }
      nodeKeyGraphRef.current[node.key] = nodeKeyGraphRef.current[node.key] || {
        parent: parent ?? null,
        children: node?.children,
      };
      const nodeDisabled = compDisabled || disabled || parentDisabled;
      const finalLabelExtra = labelExtraRender
        ? labelExtraRender(node, checked, nodeDisabled)
        : labelExtra;
      const content = (
        <>
          <Checkbox
            disabled={nodeDisabled}
            checked={checked}
            indeterminate={indeterminate}
            onChange={(evt) => {
              const { checked } = evt.target;
              const resultKeys = calcFinalSelectedKeys(node, checked);
              const selectedNodes = getAllNodesOfSelectedKeys(dataSource, resultKeys);
              onChange?.(resultKeys, selectedNodes, {
                node,
                checked,
                selectedDataSet: compact(selectedNodes?.map((r) => r?.raw) || []),
              });
            }}
          >
            {labelRender ? labelRender(node, checked, nodeDisabled) : node?.label}
          </Checkbox>
          {labelSuffixRender && labelSuffixRender(node, checked, nodeDisabled)}
          {finalLabelExtra ? <div>{finalLabelExtra}</div> : null}
        </>
      );
      const others =
        node.children?.map?.((n, idx) =>
          nodeRender({ ...n, parentChecked: checked }, node, deep + 1),
        ) ?? null;
      const shouldWrapDl = isRoot || hasChild;
      if (shouldWrapDl) {
        const ul = (
          <Ul key={`$$tree_${node.value}_${key}`}>
            <li key={`$$tree_leave_${node.value}_${key}`}>
              {intent}
              {content}
            </li>
            <section>{others}</section>
          </Ul>
        );
        return !isRoot ? (
          <li key={`$$tree_leave_wrap_${node.value}_${key}`} className="li__with_child">
            {ul}
          </li>
        ) : (
          ul
        );
      }

      return <li key={`$$tree_leave_${node.value}_${key}`}>{content}</li>;
    },
  );
  return <div className={className}>{nodeRender(dataSource)}</div>;
};
export default CheckboxTree;
